// Token rendering utilities

import {
  createConditionIcon,
  createFlameIcon,
  createLightSourceIcon,
  createSkullIcon,
  getConditionBgColor,
  getConditionPriority,
  isImageLoading,
  lightSourceSlugForItemName,
  loadImage,
  OVERLAY_PRIORITY,
} from './image-cache.js';

/**
 * @typedef {import('../types/jsdoc-types.js').Token} Token
 * @typedef {import('../types/jsdoc-types.js').Character} Character
 */

/**
 * @typedef {'tiny' | 'small' | 'medium' | 'large' | 'huge' | 'gargantuan'} TokenSize
 * @typedef {'down' | 'up' | 'left' | 'right' | 'down-left' | 'down-right' | 'up-left' | 'up-right'} TokenDirection
 * @typedef {'hostile' | 'friendly' | 'neutral' | 'mine' | 'mine-subtle' | 'targetable-hostile' | 'targetable-friendly' | 'targetable-any'} TokenIndicatorType
 * @typedef {'hostile' | 'friendly' | 'any' | 'selfOrEmpty'} TargetingFilter
 */

const TOKEN_COLOR = '#4a9eff';

/** @type {Record<TokenIndicatorType, {border: string, fill: string}>} */
const INDICATOR_COLORS = {
  hostile: { border: '#ef4444', fill: 'rgba(239, 68, 68, 0.3)' },
  friendly: { border: '#22c55e', fill: 'rgba(34, 197, 94, 0.3)' },
  neutral: { border: '#9ca3af', fill: 'rgba(156, 163, 175, 0.3)' },
  mine: { border: '#0d6efd', fill: 'rgba(13, 110, 253, 0.5)' },
  'mine-subtle': { border: 'rgba(13, 110, 253, 0.65)', fill: 'rgba(13, 110, 253, 0.2)' },
  'targetable-hostile': { border: '#ef4444', fill: 'rgba(239, 68, 68, 0.45)' },
  'targetable-friendly': { border: '#22c55e', fill: 'rgba(34, 197, 94, 0.45)' },
  'targetable-any': { border: '#0d6efd', fill: 'rgba(13, 110, 253, 0.45)' },
};

/** @type {Record<TokenDirection, number>} */
const ROTATION_ANGLES = {
  down: 0,
  right: -Math.PI / 2,
  up: Math.PI,
  left: Math.PI / 2,
  'down-right': -Math.PI / 4,
  'down-left': Math.PI / 4,
  'up-right': (-3 * Math.PI) / 4,
  'up-left': (3 * Math.PI) / 4,
};

/**
 * Get token size multiplier
 * @param {TokenSize} size - Token size
 * @returns {number}
 */
function getTokenSizeMultiplier(size) {
  switch (size) {
    case 'tiny':
      return 0.5;
    case 'small':
      return 1.0;
    case 'medium':
      return 1.0;
    case 'large':
      return 2.0;
    case 'huge':
      return 3.0;
    case 'gargantuan':
      return 4.0;
    default:
      return 1.0;
  }
}

/**
 * Draw token indicator circle
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x - Center X
 * @param {number} y - Center Y
 * @param {number} radius
 * @param {number} zoom
 * @param {TokenIndicatorType} type
 */
function drawTokenIndicator(ctx, x, y, radius, zoom, type) {
  const colors = INDICATOR_COLORS[type];
  const borderWidth = 3;

  // Draw transparent background
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, 2 * Math.PI);
  ctx.fillStyle = colors.fill;
  ctx.fill();

  // Draw border
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, 2 * Math.PI);
  ctx.strokeStyle = colors.border;
  ctx.lineWidth = borderWidth / zoom;
  ctx.stroke();
}

/**
 * Draw a single token
 * @param {CanvasRenderingContext2D} ctx
 * @param {Token} token
 * @param {object} options
 * @param {number} options.gridSize
 * @param {number} options.zoom
 * @param {boolean} [options.isSelected=false]
 * @param {boolean} [options.isHovered=false]
 * @param {number} [options.alpha=1.0]
 * @param {boolean} [options.isTargetable=false]
 * @param {TargetingFilter} [options.targetableFilter]
 * @param {string} [options.targetableRangeType] - "reach" (melee) or "ranged"; gates the long-range DIS check (melee never has long range)
 * @param {string} [options.targetableCursorBase] - server-picked cursor icon family ("swords" / "target" / "spell"); the client appends the filter color suffix
 * @param {number} [options.targetableNormalCells] - normal range in cells; targets beyond this but within long range render the long-range variant
 * @param {number} [options.targetableLongCells] - long range in cells (ranged only)
 * @param {number} [options.targetableAttackerCol] - attacker col, for distance math
 * @param {number} [options.targetableAttackerRow] - attacker row, for distance math
 * @param {boolean} [options.targetingActive=false] - When true, suppress the
 *   mine-subtle and generic hover indicators on non-targetable tokens so the
 *   targeting UI isn't polluted by unrelated rings.
 * @param {boolean} [options.showHealthBar=false]
 * @param {number} [options.currentHp]
 * @param {number} [options.maxHp]
 * @param {string} [options.currentUserId]
 * @param {string} [options.backendUrl]
 * @param {boolean} [options.showName]
 * @param {boolean} [options.skipNameLabel] - When true, skip the inline name pill so a second pass after fog can render labels on top.
 * @param {boolean} [options.darkvisionPromoted=false] - When true, the token's cell is being perceived only via the viewer's darkvision; render in greyscale per SRD 5.2.1.
 * @param {string} [options.lightSourceSlug] - Per-source light icon slug ("torch", "lantern", "candle") for PC tokens whose inventory carries a lit item. Empty/undefined falls back to the generic flame for NPCs with `givesLight`.
 */
function drawToken(ctx, token, options) {
  const {
    gridSize,
    zoom,
    isSelected = false,
    isHovered = false,
    alpha = 1.0,
    isTargetable = false,
    targetingActive = false,
    showHealthBar = false,
    currentHp,
    maxHp,
    currentUserId,
    backendUrl,
    showName = false,
    darkvisionPromoted = false,
  } = options;

  const sizeMultiplier = getTokenSizeMultiplier(/** @type {TokenSize} */ (token.size || 'medium'));
  const tokenSize = gridSize * sizeMultiplier;
  const tokenRadius = tokenSize / 2;

  // Calculate token center position (with optional pixel offset for seat alignment)
  const offset = tokenRadius;
  const tokenX = token.col * gridSize + offset + (token.offsetX || 0);
  const tokenY = token.row * gridSize + offset + (token.offsetY || 0);

  ctx.save();
  ctx.globalAlpha = alpha;

  // Placeholders (#253) are sensed-but-unseen monsters. The server
  // ships them as ordinary tokens with name="Shape in darkness" and
  // imageUrl swapped to the generic shape-in-darkness art, so they
  // render through the normal token path and pick up the standard
  // hover ring, targeting cursor, defeat overlay, and DIS badge
  // without any client-side glyph work. We deliberately do NOT draw
  // the HP bar, direction triangle, or condition icons for
  // placeholders — those would leak state about a creature the
  // player can't actually see — but the rest is identical to a
  // normal monster token.

  // Apply grayscale for defeated tokens
  if (token.defeated) {
    ctx.globalAlpha = alpha * 0.5;
    ctx.filter = 'grayscale(100%)';
  } else if (darkvisionPromoted) {
    // Per SRD 5.2.1, things perceived only via darkvision are seen "in
    // shades of grey." Apply the same grayscale filter as defeated, but
    // without the dimming alpha — the token is still in plain view, just
    // monochrome.
    ctx.filter = 'grayscale(100%)';
  } else if (token.conditions?.invisible) {
    // Invisible (from Hide): dim the token so the player can see where
    // their PC is on the map while still reading at a glance that the
    // PC is concealed. Pairs with the invisible badge in the overlay
    // track. NPCs can't see Invisible PCs anyway, so this only ever
    // visibly affects party-side rendering.
    ctx.globalAlpha = alpha * 0.4;
  }

  // Draw selection/hover indicator. During attack-targeting we suppress
  // the per-token rings entirely — the cell highlight under the token
  // already conveys "this is targetable" in the filter color, and the
  // hover cursor + DIS badge land on top of that. Adding a ring on the
  // token doubles the visual without adding info.
  const isMyToken = token.playerId && currentUserId && token.playerId === currentUserId;
  if (!targetingActive) {
    if (isMyToken && !isSelected && !isHovered) {
      drawTokenIndicator(ctx, tokenX, tokenY, tokenRadius, zoom, 'mine-subtle');
    }
    if (isSelected || isHovered) {
      const indicatorType = /** @type {TokenIndicatorType} */ (isMyToken ? 'mine' : 'neutral');
      drawTokenIndicator(ctx, tokenX, tokenY, tokenRadius, zoom, indicatorType);
    }
  }

  // Draw token background (circle)
  if (token.hasCircleBorder) {
    ctx.beginPath();
    ctx.arc(tokenX, tokenY, tokenRadius, 0, 2 * Math.PI);
    ctx.fillStyle = '#fff';
    ctx.fill();
  }

  // Draw token image
  ctx.save();

  // Circular clipping
  if (token.hasCircleBorder) {
    ctx.beginPath();
    ctx.arc(tokenX, tokenY, tokenRadius, 0, 2 * Math.PI);
    ctx.clip();
  }

  // Apply rotation based on direction
  const rotationAngle = ROTATION_ANGLES[/** @type {TokenDirection} */ (token.direction || 'down')];
  ctx.translate(tokenX, tokenY);
  ctx.rotate(rotationAngle);
  ctx.translate(-tokenX, -tokenY);

  // Load and draw image
  let imageUrl = token.imageUrl;
  if (imageUrl?.startsWith('/') && backendUrl) {
    imageUrl = backendUrl + imageUrl;
  }

  const img = imageUrl ? loadImage(imageUrl) : null;
  if (img?.complete && img.naturalWidth > 0) {
    ctx.drawImage(img, tokenX - tokenRadius, tokenY - tokenRadius, tokenSize, tokenSize);
  } else if (imageUrl && isImageLoading(imageUrl)) {
    // Image is still being fetched (typical on a game refresh while the
    // token cache is cold). Leave the interior blank for this frame so
    // we don't flash the blue-square placeholder before the real token
    // pops in — the render loop redraws every frame so the token will
    // appear as soon as the load completes.
  } else {
    // Fallback placeholder — used when the token has no imageUrl at all
    // or the load errored. Genuinely missing image, not "still loading".
    const initial = token.name ? token.name.charAt(0).toUpperCase() : '?';
    ctx.fillStyle = TOKEN_COLOR;
    ctx.fillRect(tokenX - tokenRadius, tokenY - tokenRadius, tokenSize, tokenSize);

    const fontSize = tokenSize * 0.4;
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initial, tokenX, tokenY);
  }

  ctx.restore();

  // Draw border
  if (token.hasCircleBorder) {
    ctx.beginPath();
    ctx.arc(tokenX, tokenY, tokenRadius, 0, 2 * Math.PI);
    ctx.strokeStyle = token.borderColor || '#666';
    ctx.lineWidth = 3 / zoom;
    ctx.stroke();
  }

  // Targeting hover icon: Lucide "target" overlaid on top of the token
  // image so it's legible against the artwork. Pre-rasterized per color
  // under public/assets/icons (see scripts/generate-icons.mjs).
  if (isTargetable && isHovered) {
    const isMelee = options.targetableRangeType === 'reach';
    // Cursor itself is white; the cell highlight under it carries the
    // filter color.
    const iconName = `${options.targetableCursorBase}-white`;
    const icon = loadImage(`/assets/icons/${iconName}.png`);
    if (icon?.complete && icon.naturalWidth > 0) {
      const iconSize = tokenSize * 0.7;
      // Disadvantage triggers for a hovered token target:
      //   - Long-range band (beyond normal but within long; ranged
      //     weapons only — melee weapons don't have a long range).
      //   - Placeholder target (sensed-but-unseen creature; 5e SRD:
      //     attacks against a target you can't see have disadvantage).
      const normal = options.targetableNormalCells;
      const long = options.targetableLongCells;
      const aCol = options.targetableAttackerCol;
      const aRow = options.targetableAttackerRow;
      const inLongRange =
        !isMelee &&
        normal != null &&
        long != null &&
        aCol != null &&
        aRow != null &&
        long > normal &&
        Math.max(Math.abs(token.col - aCol), Math.abs(token.row - aRow)) > normal;
      const isPlaceholderTarget = !!token.placeholder;
      const showDisadv = inLongRange || isPlaceholderTarget;
      ctx.drawImage(icon, tokenX - iconSize / 2, tokenY - iconSize / 2, iconSize, iconSize);

      if (showDisadv) {
        const label = 'DIS';
        const fontPx = Math.max(8, gridSize * 0.16);
        ctx.save();
        ctx.font = `700 ${fontPx}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const padX = fontPx * 0.45;
        const padY = fontPx * 0.2;
        const textWidth = ctx.measureText(label).width;
        const badgeW = textWidth + padX * 2;
        const badgeH = fontPx + padY * 2;
        const cellBottom = (token.row + 1) * gridSize;
        const badgeMargin = Math.max(2, gridSize * 0.04);
        const badgeY = cellBottom - badgeH - badgeMargin;
        const badgeX = tokenX - badgeW / 2;
        const radius = badgeH / 2;
        ctx.fillStyle = '#dc3545';
        ctx.beginPath();
        ctx.moveTo(badgeX + radius, badgeY);
        ctx.arcTo(badgeX + badgeW, badgeY, badgeX + badgeW, badgeY + badgeH, radius);
        ctx.arcTo(badgeX + badgeW, badgeY + badgeH, badgeX, badgeY + badgeH, radius);
        ctx.arcTo(badgeX, badgeY + badgeH, badgeX, badgeY, radius);
        ctx.arcTo(badgeX, badgeY, badgeX + badgeW, badgeY, radius);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.fillText(label, tokenX, badgeY + badgeH / 2);
        ctx.restore();
      }
    }
  }

  // Draw direction indicator (suppressed on placeholders — facing is
  // information about a creature the player can't actually see)
  if (!token.placeholder) {
    ctx.save();
    ctx.translate(tokenX, tokenY);
    ctx.rotate(ROTATION_ANGLES[/** @type {TokenDirection} */ (token.direction || 'down')]);

    const triangleWidth = tokenRadius / 3;
    const triangleHeight = tokenRadius / 5;

    ctx.beginPath();
    ctx.moveTo(0, tokenRadius);
    ctx.lineTo(-triangleWidth / 2, tokenRadius - triangleHeight);
    ctx.lineTo(triangleWidth / 2, tokenRadius - triangleHeight);
    ctx.closePath();

    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.lineWidth = 1.5 / zoom;
    ctx.stroke();

    ctx.restore();
  }

  // Name labels are drawn in a second pass after the fog / darkness
  // overlay so they stay readable when tokens sit in dim or dark
  // cells. See drawTokenLabels at the bottom of this file. The block
  // below is gated on the legacy condition for callers that haven't
  // migrated to the second-pass call yet, but production renderers
  // skip it via skipNameLabel.
  if ((isHovered || showName) && !options.skipNameLabel) {
    const fontSize2 = Math.max(12, 16 / zoom);
    ctx.font = `${fontSize2}px Arial`;
    ctx.textAlign = 'center';

    const textMetrics = ctx.measureText(token.name);
    const padX = 8 / zoom;
    const padY = 5 / zoom;
    const backgroundWidth = textMetrics.width + padX * 2;
    const backgroundHeight = fontSize2 + padY * 2;

    const nameMargin = 4 / zoom;
    const nameY = token.row === 0 ? (token.row + 1) * gridSize + nameMargin : token.row * gridSize - nameMargin;
    const textBaseline = token.row === 0 ? 'top' : 'bottom';
    ctx.textBaseline = textBaseline;

    const backgroundY = textBaseline === 'bottom' ? nameY - backgroundHeight : nameY;
    // beginPath() so fill() only fills this pill (see drawTokenLabels
    // for the same fix on the second-pass path).
    ctx.beginPath();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.roundRect(tokenX - backgroundWidth / 2, backgroundY, backgroundWidth, backgroundHeight, 4 / zoom);
    ctx.fill();

    ctx.fillStyle = 'white';
    const textY = textBaseline === 'bottom' ? nameY - padY : nameY + padY;
    ctx.fillText(token.name, tokenX, textY);
  }

  // Draw health bar (suppressed on placeholders — HP for a sensed-
  // but-unseen creature would leak state the player shouldn't have).
  // At 100% zoom and above the bar is tall enough to fit a "X / Y"
  // readout centered inside; zoomed out the bar shrinks back down
  // (the readout is suppressed too, since text becomes unreadable).
  if (!token.placeholder && showHealthBar && currentHp !== undefined && maxHp !== undefined && maxHp > 0) {
    const showReadout = zoom >= 1;
    const barHeight = (showReadout ? 17 : 6) / zoom;
    const barPadding = 4 / zoom;
    const barWidth = gridSize - barPadding * 2;

    const tokenBottomRow = token.row + Math.ceil(sizeMultiplier) - 1;
    const barY = (tokenBottomRow + 1) * gridSize - barHeight - barPadding;
    const barX = token.col * gridSize + barPadding;

    const hpPercent = currentHp / maxHp;
    const barColor = hpPercent > 0.5 ? '#22c55e' : hpPercent > 0.25 ? '#eab308' : '#ef4444';

    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(barX, barY, barWidth, barHeight);

    ctx.fillStyle = barColor;
    ctx.fillRect(barX, barY, barWidth * hpPercent, barHeight);

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.lineWidth = 1 / zoom;
    ctx.strokeRect(barX, barY, barWidth, barHeight);

    // "X / Y" readout centered in the bar. White fill with a thin
    // black stroke so it stays readable against the green / yellow /
    // red fill regardless of which color the bar happens to be. Only
    // shown at 100% zoom and above — zoomed out the text becomes
    // illegible noise; the bar fill alone carries the at-a-glance
    // state.
    if (showReadout) {
      const fontPx = 13 / zoom;
      ctx.font = `bold ${fontPx}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const label = `${Math.max(0, Math.round(currentHp))} / ${Math.round(maxHp)}`;
      const textX = barX + barWidth / 2;
      const textY = barY + barHeight / 2;
      ctx.lineWidth = 2 / zoom;
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
      ctx.strokeText(label, textX, textY);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(label, textX, textY);
    }
  }

  // Unified token-overlay track (#371). All status indicators —
  // skull, conditions, light source — share a single track flowing
  // top-right-to-left. Placeholders (sensed-but-unseen monsters) get
  // no overlays at all to avoid leaking state about a creature the
  // player can't actually see.
  if (!token.placeholder) {
    drawTokenOverlayTrack(ctx, token, {
      gridSize,
      tokenSize,
      lightSourceSlug: options.lightSourceSlug,
    });
  }

  ctx.restore();
}

/**
 * Build the prioritized list of overlay items for a token. Ordering is
 * by `OVERLAY_PRIORITY` ascending so the highest-priority items render
 * closest to the top-right corner. Defeated tokens collapse to just
 * the skull — the grayscale + dimmed alpha already convey "dead", and
 * conditions aren't actionable on a defeated actor.
 *
 * Exported for unit tests. The renderer calls it internally.
 *
 * @param {Token} token
 * @param {string|undefined} lightSourceSlug
 * @returns {Array<{kind:'skull'|'condition'|'light', priority:number, condition?:string, slug?:string}>}
 */
export function buildOverlayItems(token, lightSourceSlug) {
  /** @type {Array<{kind:'skull'|'condition'|'light', priority:number, condition?:string, slug?:string}>} */
  const items = [];

  if (token.defeated) {
    items.push({ kind: 'skull', priority: OVERLAY_PRIORITY.defeated });
    return items;
  }

  if (token.conditions) {
    for (const [condition, active] of Object.entries(token.conditions)) {
      if (!active) continue;
      items.push({
        kind: 'condition',
        condition,
        priority: getConditionPriority(condition),
      });
    }
  }

  // Light source — PC slug from inventory wins; otherwise NPC givesLight
  // falls back to a flame glyph.
  if (lightSourceSlug) {
    items.push({ kind: 'light', priority: OVERLAY_PRIORITY.light, slug: lightSourceSlug });
  } else if (token.givesLight && token.brightRadius && token.brightRadius > 0) {
    items.push({ kind: 'light', priority: OVERLAY_PRIORITY.light, slug: 'flame' });
  }

  items.sort((a, b) => a.priority - b.priority);
  return items;
}

/**
 * Render the unified overlay track at the top-right corner of a
 * token's grid square, walking leftward. Caps at 4 visible badges; if
 * more apply, slot 4 becomes a "+N" overflow pill (same circle bg,
 * text instead of an icon).
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Token} token
 * @param {{gridSize:number, tokenSize:number, lightSourceSlug?:string}} options
 */
function drawTokenOverlayTrack(ctx, token, options) {
  const { gridSize, lightSourceSlug } = options;
  const items = buildOverlayItems(token, lightSourceSlug);
  if (items.length === 0) return;

  // Badge size is 1/6 of the grid cell — small enough that 4 badges
  // fit along the top edge with margin (~4 × 0.23 = 0.92 of the cell)
  // but readable enough that the icon shape stays legible at typical
  // zoom levels. Icon-to-badge ratio (0.7) matches the legacy
  // single-corner badges so the new track inherits their proportions.
  const iconSize = gridSize / 6;
  const bgRadius = iconSize * 0.7;
  const stride = bgRadius * 2 + gridSize * 0.02;

  const sizeMultiplier = getTokenSizeMultiplier(/** @type {TokenSize} */ (token.size || 'medium'));
  const squareRight = (token.col + sizeMultiplier) * gridSize;
  const squareTop = token.row * gridSize;
  let badgeX = squareRight - bgRadius - gridSize * 0.05;
  const badgeY = squareTop + bgRadius + gridSize * 0.05;

  const visibleSlots = Math.min(items.length, 4);
  const overflowing = items.length > 4;

  // Reset filter/alpha so badges aren't dimmed by the defeated/grayscale
  // state still active on the canvas context.
  ctx.filter = 'none';
  ctx.globalAlpha = 0.95;

  for (let i = 0; i < visibleSlots; i++) {
    const isLastSlot = i === visibleSlots - 1;
    if (overflowing && isLastSlot) {
      drawOverflowBadge(ctx, badgeX, badgeY, bgRadius, iconSize, items.length - (visibleSlots - 1));
    } else {
      drawOverlayBadge(ctx, items[i], badgeX, badgeY, bgRadius, iconSize);
    }
    badgeX -= stride;
  }
}

/**
 * Render a single overlay badge — circle bg + icon. Skips drawing
 * when the icon image hasn't loaded yet; the next render frame picks
 * it up once cached.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {{kind:'skull'|'condition'|'light', condition?:string, slug?:string}} item
 * @param {number} x
 * @param {number} y
 * @param {number} bgRadius
 * @param {number} iconSize
 */
function drawOverlayBadge(ctx, item, x, y, bgRadius, iconSize) {
  let img = null;
  let bgColor = 'rgba(0, 0, 0, 0.85)';

  if (item.kind === 'skull') {
    img = createSkullIcon();
    bgColor = 'rgba(0, 0, 0, 0.85)';
  } else if (item.kind === 'condition' && item.condition) {
    img = createConditionIcon(item.condition);
    bgColor = getConditionBgColor(item.condition);
  } else if (item.kind === 'light') {
    img = item.slug && item.slug !== 'flame' ? createLightSourceIcon(item.slug) : createFlameIcon();
    bgColor = 'rgba(0, 0, 0, 0.85)';
  }

  if (!img || !img.complete) return;

  ctx.beginPath();
  ctx.arc(x, y, bgRadius, 0, Math.PI * 2);
  ctx.fillStyle = bgColor;
  ctx.fill();

  ctx.drawImage(img, x - iconSize / 2, y - iconSize / 2, iconSize, iconSize);
}

/**
 * Render the +N overflow pill in the last visible slot when more than
 * 4 overlays apply. Uses the same circle bg as a normal badge with the
 * count text centered in place of an icon.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} bgRadius
 * @param {number} iconSize
 * @param {number} count
 */
function drawOverflowBadge(ctx, x, y, bgRadius, iconSize, count) {
  ctx.beginPath();
  ctx.arc(x, y, bgRadius, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
  ctx.fill();

  ctx.fillStyle = '#fff';
  ctx.font = `bold ${Math.round(iconSize * 0.85)}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`+${count}`, x, y);
}

/**
 * Draw all tokens
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<Token>} tokens
 * @param {object} options
 * @param {number} options.gridSize
 * @param {number} options.zoom
 * @param {number|null} [options.selectedTokenIndex]
 * @param {number|null} [options.hoveredTokenIndex]
 * @param {Array<string>} [options.targetableTokenIds=[]]
 * @param {TargetingFilter} [options.targetableFilter] - drives ring color
 * @param {string} [options.targetableRangeType] - "reach" (melee) or "ranged"; gates the long-range DIS check (melee never has long range)
 * @param {string} [options.targetableCursorBase] - server-picked cursor icon family ("swords" / "target" / "spell"); the client appends the filter color suffix
 * @param {number} [options.targetableNormalCells] - normal range in cells; targets beyond this but within long range render the long-range cursor variant
 * @param {number} [options.targetableLongCells] - long range in cells (ranged only)
 * @param {number} [options.targetableAttackerCol] - attacker col, for distance math
 * @param {number} [options.targetableAttackerRow] - attacker row, for distance math
 * @param {boolean} [options.targetingActive=false] - suppress mine/hover
 *   rings on non-targetable tokens while targeting is in progress.
 * @param {boolean} [options.combatActive=false]
 * @param {string} [options.currentUserId]
 * @param {string} [options.backendUrl]
 * @param {Array<Character>} [options.characters=[]]
 * @param {boolean} [options.showNames=false]
 * @param {boolean} [options.skipNameLabel=false] - When true, skip per-token name pills so the caller can run drawTokenLabels in a second pass after fog/darkness.
 * @param {Set<string>|null} [options.darkvisionPromotedCells=null] - Set of "col,row" keys identifying cells whose illumination was promoted by the viewer's darkvision; tokens on those cells render greyscale.
 */
export function drawTokens(ctx, tokens, options) {
  const {
    gridSize,
    zoom,
    selectedTokenIndex = null,
    hoveredTokenIndex = null,
    targetableTokenIds = [],
    targetableFilter,
    targetableRangeType,
    targetableCursorBase,
    targetableNormalCells,
    targetableLongCells,
    targetableAttackerCol,
    targetableAttackerRow,
    targetingActive = false,
    combatActive = false,
    currentUserId,
    backendUrl,
    characters = [],
    showNames = false,
    skipNameLabel = false,
    /** @type {Set<string>|null} keyed "col,row" for cells whose illumination was promoted by the viewer's darkvision */
    darkvisionPromotedCells = null,
  } = options;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (!token) continue;
    const isSelected = i === selectedTokenIndex;
    const isHovered = i === hoveredTokenIndex;
    const isTargetable = targetableTokenIds.includes(token.id);

    // Get HP for health bar. Monster tokens carry their currentHP /
    // maxHP directly on the placement; PC tokens were built with
    // pc.hp / pc.maxHp normalized into currentHP / maxHP by the
    // render-pipeline. The legacy templateId lookup against
    // `characters` historically only fired for tokens that referenced
    // a character by templateId — preserve that path so any consumer
    // relying on it keeps working, but DON'T fall back to token.id
    // for the HP read: PC character records use {hp, maxHp} keys
    // (not {currentHP, maxHP}), so the override would clobber the
    // working values with undefined.
    let currentHp = token.currentHP;
    let maxHp = token.maxHP;
    const monsterTemplateCharacter = characters.find((c) => c.id === token.templateId);
    if (monsterTemplateCharacter) {
      currentHp = monsterTemplateCharacter.currentHP;
      maxHp = monsterTemplateCharacter.maxHP;
    }

    // For #371's PC light-source overlay we need the PC's character
    // record (so we can read inventory). Look that up by token.id —
    // PC tokens are built with token.id === pc.id.
    const ownerCharacter = monsterTemplateCharacter || characters.find((c) => c.id === token.id);
    const lightSourceSlug = pickLightSourceSlug(ownerCharacter);

    const darkvisionPromoted = !!darkvisionPromotedCells && darkvisionPromotedCells.has(`${token.col},${token.row}`);

    drawToken(ctx, token, {
      gridSize,
      zoom,
      isSelected,
      isHovered,
      isTargetable,
      targetableFilter,
      targetableRangeType,
      targetableCursorBase,
      targetableNormalCells,
      targetableLongCells,
      targetableAttackerCol,
      targetableAttackerRow,
      targetingActive,
      showHealthBar: combatActive,
      currentHp,
      maxHp,
      currentUserId,
      backendUrl,
      showName: showNames,
      skipNameLabel,
      darkvisionPromoted,
      lightSourceSlug,
    });
  }
}

/**
 * Pick the per-source light icon slug for a token (#371). PCs derive
 * the slug from the lit item in their inventory — torch/lantern/candle
 * — so the overlay matches the equipment-action icon used to light
 * it. When a spell-applied light source (e.g. the Light cantrip from
 * #477) is active and the inventory has no recognized lit item, falls
 * back to the generic flame slug so the player still sees an emitter
 * badge. NPCs return "" since their `givesLight` flag drives the same
 * flame fallback in `drawTokenOverlayTrack`.
 *
 * Exported for unit tests. The renderer calls it internally.
 *
 * @param {Character|undefined} character
 * @returns {string} slug, or "" when nothing on this PC emits light
 */
export function pickLightSourceSlug(character) {
  if (!character) return '';
  if (Array.isArray(character.inventory)) {
    for (const item of character.inventory) {
      if (!item) continue;
      if (item.lit !== true) continue;
      const slug = lightSourceSlugForItemName(item.name);
      if (slug) return slug;
    }
  }
  if (Array.isArray(character.spellActiveEffects)) {
    for (const effect of character.spellActiveEffects) {
      if (!effect) continue;
      const bright = Number(effect.brightRadius) || 0;
      const dim = Number(effect.dimRadius) || 0;
      if (bright > 0 || dim > 0) return 'flame';
    }
  }
  return '';
}

/**
 * Draw shadow/ghost token for movement preview
 * @param {CanvasRenderingContext2D} ctx
 * @param {Token} token
 * @param {object} options
 * @param {number} options.gridSize
 * @param {number} options.zoom
 * @param {boolean} [options.showIndicator=false]
 */
export function drawShadowToken(ctx, token, options) {
  const { gridSize, zoom, showIndicator = false } = options;
  drawToken(ctx, token, {
    gridSize,
    zoom,
    isSelected: showIndicator,
    isHovered: showIndicator,
    alpha: 0.5,
  });
}

/**
 * Second-pass label rendering. Run AFTER the fog / darkness overlay
 * so token labels stay readable when their tokens sit in dim or
 * unlit cells. Mirrors the conditional that the per-token label
 * block used (hover or always-show), and substitutes "Shape in
 * darkness" for placeholder placements (#253).
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Token[]} tokens
 * @param {object} options
 * @param {number} options.gridSize
 * @param {number} options.zoom
 * @param {number|null} [options.hoveredTokenIndex=null]
 * @param {boolean} [options.showNames=false]
 */
export function drawTokenLabels(ctx, tokens, options) {
  const { gridSize, zoom, hoveredTokenIndex = null, showNames = false } = options;
  const fontSize = Math.max(12, 16 / zoom);
  const padX = 8 / zoom;
  const padY = 5 / zoom;
  const nameMargin = 4 / zoom;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (!token) continue;
    const isHovered = i === hoveredTokenIndex;
    if (!isHovered && !showNames) continue;
    // Placeholder labels come pre-redacted from the server (name is
    // already "Shape in darkness"), so we can read token.name in
    // every case.
    const labelText = token.name;
    if (!labelText) continue;

    ctx.save();
    ctx.font = `${fontSize}px Arial`;
    ctx.textAlign = 'center';
    const sizeMultiplier = getTokenSizeMultiplier(/** @type {TokenSize} */ (token.size || 'medium'));
    const tokenSize = gridSize * sizeMultiplier;
    const tokenX = token.col * gridSize + tokenSize / 2 + (token.offsetX || 0);
    const textMetrics = ctx.measureText(labelText);
    const backgroundWidth = textMetrics.width + padX * 2;
    const backgroundHeight = fontSize + padY * 2;
    const nameY = token.row === 0 ? (token.row + 1) * gridSize + nameMargin : token.row * gridSize - nameMargin;
    const textBaseline = token.row === 0 ? 'top' : 'bottom';
    ctx.textBaseline = textBaseline;
    const backgroundY = textBaseline === 'bottom' ? nameY - backgroundHeight : nameY;
    // Start a fresh path so fill() only fills this pill — without the
    // beginPath, fill() would also re-fill any prior subpath left active
    // in the context (e.g. the placeholder glyph's arc drawn moments
    // earlier), darkening tokens that share the canvas state.
    ctx.beginPath();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
    ctx.roundRect(tokenX - backgroundWidth / 2, backgroundY, backgroundWidth, backgroundHeight, 4 / zoom);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    const textY = textBaseline === 'bottom' ? nameY - padY : nameY + padY;
    ctx.fillText(labelText, tokenX, textY);
    ctx.restore();
  }
}
